<script setup lang="ts">
import { route } from 'ziggy-js';
import { useForm } from '@inertiajs/vue3';
import { AuthenticationCardLogo } from '@/Components/Brand';
import { Checkbox, InputError, InputLabel, PrimaryButton, TextInput } from '@/Components/Form';
import { AnchorLink } from '@/Components/Frontend/Navigation';
import AuthLayout from '@/Layouts/AuthLayout.vue';
import { AuthenticationCard } from '@/Layouts/Partials';
import GoogleButton from '@/Pages/Auth/Partials/GoogleButton.vue';

const form = useForm({
	name: '',
	surname: '',
	email: '',
	password: '',
	password_confirmation: '',
	terms: false,
});

const submit = () => {
	form.post(route('register'), {
		onFinish: () => form.reset('password', 'password_confirmation'),
	});
};
</script>

<template>
	<AuthLayout :title="$t('Register')">
		<AuthenticationCard>
			<template #logo>
				<AuthenticationCardLogo />
			</template>

			<form @submit.prevent="submit">
				<div>
					<InputLabel name="name" :value="$t('Name')" />
					<TextInput
						v-model="form.name"
						name="name"
						type="text"
						class="mt-1 block w-full"
						required
						autofocus
						autocomplete="given-name"
					/>
					<InputError class="mt-2" :message="form.errors.name" />
				</div>

				<div class="mt-4">
					<InputLabel name="surname" :value="$t('Surname')" />
					<TextInput
						v-model="form.surname"
						name="surname"
						type="text"
						class="mt-1 block w-full"
						required
						autocomplete="family-name"
					/>
					<InputError class="mt-2" :message="form.errors.surname" />
				</div>

				<div class="mt-4">
					<InputLabel name="email" :value="$t('Email')" />
					<TextInput
						v-model="form.email"
						name="email"
						type="email"
						class="mt-1 block w-full"
						required
						autocomplete="username"
					/>
					<InputError class="mt-2" :message="form.errors.email" />
				</div>

				<div class="mt-4">
					<InputLabel name="password" :value="$t('Password')" />
					<TextInput
						v-model="form.password"
						name="password"
						type="password"
						class="mt-1 block w-full"
						required
						autocomplete="new-password"
					/>
					<InputError class="mt-2" :message="form.errors.password" />
				</div>

				<div class="mt-4">
					<InputLabel name="password_confirmation" :value="$t('Confirm Password')" />
					<TextInput
						v-model="form.password_confirmation"
						name="password_confirmation"
						type="password"
						class="mt-1 block w-full"
						required
						autocomplete="new-password"
					/>
					<InputError class="mt-2" :message="form.errors.password_confirmation" />
				</div>

				<div v-if="$page.props.jetstream.hasTermsAndPrivacyPolicyFeature" class="mt-4">
					<InputLabel name="terms">
						<div class="flex items-center">
							<Checkbox v-model:checked="form.terms" name="terms" required />

							<div class="ms-2">
								{{ $t('I agree to the') }}
								<AnchorLink :label="$t('Terms of Service')" target="_blank" :href="route('admin.dashboard')"
									>{{ $t('Terms of Service') }}
								</AnchorLink>
								{{ $t('tos_end') }}
								<AnchorLink :label="$t('Privacy Policy')" target="_blank" :href="route('admin.dashboard')"
									>{{ $t('Privacy Policy') }}
								</AnchorLink>
							</div>
						</div>
						<InputError class="mt-2" :message="form.errors.terms" />
					</InputLabel>
				</div>

				<div class="flex items-center justify-end mt-4">
					<AnchorLink :inertia="true" :href="route('login')" :label="$t('Already registered?')">
						{{ $t('I already have an account') }}
					</AnchorLink>

					<PrimaryButton class="ms-4" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
						{{ $t('Register') }}
					</PrimaryButton>
				</div>
			</form>
			<div class="mt-10">
				<GoogleButton :label="$t('auth.register_with_social', { provider: 'Google' })" />
			</div>
		</AuthenticationCard>
	</AuthLayout>
</template>
